'use client';

/**
 * usePageTabs Hook
 *
 * Manages page-level tab navigation with URL synchronization.
 * Supports visibility filtering based on user context.
 *
 * @see Issue #330 - Unified Navigation System
 */

import type { NavTab, PageTabsConfig, VisibilityContext } from '@ops-matrix/shared-types';
import { checkVisibility } from '@ops-matrix/shared-types';
import type React from 'react';
import { useCallback, useMemo } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface UsePageTabsOptions {
  /** Tab configuration */
  config: PageTabsConfig;

  /** Current URL search params (from router) */
  searchParams: URLSearchParams;

  /** Set search params (from router) */
  setSearchParams: (params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => void;

  /** Visibility context for filtering tabs */
  visibilityContext?: VisibilityContext;

  /** Callback when tab changes (for navigation) */
  onNavigate?: (path: string) => void;
}

export interface UsePageTabsReturn {
  /** Currently active tab ID */
  activeTab: string;

  /** Set active tab by ID */
  setActiveTab: (tabId: string) => void;

  /** Visible tabs (filtered by visibility) */
  visibleTabs: NavTab[];

  /** Get tab by ID */
  getTab: (tabId: string) => NavTab | undefined;

  /** Check if a tab is active */
  isTabActive: (tabId: string) => boolean;

  /** Handle tab change event (for GlassTabs) */
  handleTabChange: (event: React.SyntheticEvent, newValue: string | number) => void;
}

// ============================================================================
// Hook
// ============================================================================

export function usePageTabs({
  config,
  searchParams,
  setSearchParams,
  visibilityContext = {},
  onNavigate,
}: UsePageTabsOptions): UsePageTabsReturn {
  const urlParam = config.urlParam ?? 'tab';

  // Filter tabs based on visibility
  const visibleTabs = useMemo(() => {
    return config.tabs.filter((tab) => !tab.visibility || checkVisibility(tab.visibility, visibilityContext));
  }, [config.tabs, visibilityContext]);

  // Get active tab from URL or default
  const activeTab = useMemo(() => {
    const urlTab = searchParams.get(urlParam);

    // If URL has a valid tab, use it
    if (urlTab && visibleTabs.some((t) => t.id === urlTab)) {
      return urlTab;
    }

    // Fall back to configured default or first visible tab
    if (config.defaultTab && visibleTabs.some((t) => t.id === config.defaultTab)) {
      return config.defaultTab;
    }

    return visibleTabs[0]?.id ?? '';
  }, [searchParams, urlParam, visibleTabs, config.defaultTab]);

  // Set active tab
  const setActiveTab = useCallback(
    (tabId: string) => {
      const tab = visibleTabs.find((t) => t.id === tabId);
      if (!tab) return;

      // Update URL
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(urlParam, tabId);
        return next;
      });

      // Navigate to tab path if callback provided
      if (onNavigate && tab.path) {
        onNavigate(tab.path);
      }
    },
    [visibleTabs, setSearchParams, urlParam, onNavigate],
  );

  // Get tab by ID
  const getTab = useCallback(
    (tabId: string): NavTab | undefined => {
      return visibleTabs.find((t) => t.id === tabId);
    },
    [visibleTabs],
  );

  // Check if tab is active
  const isTabActive = useCallback(
    (tabId: string): boolean => {
      return activeTab === tabId;
    },
    [activeTab],
  );

  // Handle tab change event (for GlassTabs component)
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string | number) => {
      setActiveTab(String(newValue));
    },
    [setActiveTab],
  );

  return {
    activeTab,
    setActiveTab,
    visibleTabs,
    getTab,
    isTabActive,
    handleTabChange,
  };
}

export default usePageTabs;
